import { effect, inject, Injectable, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import { Auth } from '../auth/auth';

@Injectable({
  providedIn: 'root',
})
export class Posthog {
  private readonly ngZone = inject(NgZone);
  private readonly auth = inject(Auth);

  constructor() {
    this.initPosthog();
    this.trackAuthState();
  }

  private initPosthog(): void {
    this.ngZone.runOutsideAngular(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const incomingDistinctId = urlParams.get('ph_distinct_id');

      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        defaults: '2026-01-30',
        persistence: 'sessionStorage',

        bootstrap: incomingDistinctId
          ? {
              distinctID: incomingDistinctId,
              isIdentifiedID: false,
            }
          : undefined,
        session_recording: {
          maskAllInputs: true,
        },
      });

      if (incomingDistinctId) {
        this.cleanupTrackingParameters();
      }
    });
  }

  private cleanupTrackingParameters(): void {
    const url = new URL(window.location.href);

    url.searchParams.delete('ph_distinct_id');

    const cleanUrl = url.pathname + url.search + url.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  private trackAuthState(): void {
    let wasIdentified = false;

    effect(() => {
      if (!this.auth.isLoaded()) return;

      const user = this.auth.user();

      if (user && user.id) {
        const emailAddress = user.primaryEmailAddress?.emailAddress ?? '';
        const fullName = this.auth.userName();

        posthog.identify(user.id, {
          email: emailAddress,
          name: fullName,
        });

        Sentry.setUser({
          id: user.id,
          email: emailAddress,
          username: fullName,
        });

        if (!wasIdentified) {
          posthog.capture('login success');
        }

        wasIdentified = true;
      } else {
        if (wasIdentified) {
          posthog.capture('logout success');

          posthog.reset();
          Sentry.setUser(null);
          wasIdentified = false;
        }
      }
    });
  }

  public capture(eventName: string, properties?: Record<string, unknown>): void {
    posthog.capture(eventName, properties);
  }
}
